<!-- SearchUserGroup.vue -->
<!-- Allows us to search users and groups by name -->
<!-- This button has two parts -- the left side of the button is the default action, the right side displays an option menu -->
<script lang="ts" setup>
    import { ref, watch, computed } from "vue";
    import SearchBase  from './SearchBase.vue';
    import { list } from '../../lib/ChoiceApi';
    import { ISearchUserGroupItem, ISearchUserGroupInputs, ISearchUserGroupKey, ISearchBaseInputs } from "../../lib/Models/SearchModel";
    import { PrivilegeType, AppScreenNames } from '../../Enums/ChoiceEnums';

    //const emit = defineEmits(["onChange"]);
    const emit = defineEmits<{
        (e: "onChange", selectedItem: ISearchUserGroupItem): void
    }>();    

    const props = withDefaults(defineProps<{
        // Placeholder text to be displayed inside text control if no values
        placeholder?: string,
        // Label for the component
        label?: string,
        // The default search value 
        defaultSearchKey?: ISearchUserGroupKey,
        // The maximum height of the search results
        maxHeight?: number,
        // Screen Name
        screenName: AppScreenNames,
        // Reset Focus
        resetFocus?: boolean,
        // Perform auto-search on text input
        enableAutoSearch?: boolean,
        // Show the clear search function
        showClearSearch?: boolean,
        // Add to base Class
        addToBaseClass?: string
    }>(), {
        placeholder: "Search User/Group",
        label: "User/Group",
        addToBaseClass: ""
    });

    const searchColumns = ref(
        [
            { title: "Sys ID", element: "identifier" },
            { title: "Type", element: "optionalDisplay" },
            { title: "Name", element: "display" }
        ],        
    );
    const searchResults = ref<ISearchUserGroupItem[]>([]);
    const defaultSearchText = ref<string>("");
    const processing = ref<boolean>(false);
    const defaultAddToBaseClass = computed<string>(() => props.addToBaseClass);

    watch(() => props.defaultSearchKey, (newValue, oldValue) => {
        defaultSearchText.value = "";
        if (newValue?.display) {
            defaultSearchText.value = newValue.display;
            const selectedItem: ISearchUserGroupItem = {
                identifier: newValue.identifier,
                optionalIdentifier: newValue.optionalIdentifier,
                display: newValue.display
            };
            onChange(selectedItem);
        }
        else if (newValue?.identifier) {
            const searchInputs: ISearchUserGroupInputs = { 
                screenName: props.screenName,
                privilege: PrivilegeType.Read,
                optionalIdentifier: newValue.optionalIdentifier,
                identifier: newValue.identifier,
                displayAllResults: false 
            };
            processing.value = true;
            performSearch(searchInputs);
        }
        else if (newValue?.searchText) {
            defaultSearchText.value = newValue.searchText;
            const selectedItem: ISearchUserGroupItem = {};
            onChange(selectedItem);
            const searchInputs: ISearchUserGroupInputs = { 
                screenName: props.screenName,
                privilege: PrivilegeType.Read,
                searchText: newValue.searchText, 
                displayAllResults: false 
            };
            processing.value = true;
            performSearch(searchInputs);
        }
        else  {
            defaultSearchText.value = "";
            const selectedItem: ISearchUserGroupItem = {};
            onChange(selectedItem);
        }
    });

    async function performSearch(inputs: ISearchUserGroupInputs) {
        const respSearch = await list.searchUserScreenGroup(inputs);
        searchResults.value = respSearch?.list || [];
    }

    async function performSearchBase(inputs: ISearchBaseInputs)  {
        const searchInputs: ISearchUserGroupInputs = { 
            screenName: props.screenName,
            privilege: PrivilegeType.Read,
            searchText: inputs.searchText,
            displayAllResults: false 
        };
        performSearch(searchInputs);
    }

    function onChange(selectedItem: ISearchUserGroupItem) {
        emit("onChange", selectedItem);
    }

</script>

<template>
    <search-base 
        :placeholder="placeholder" 
        :label="label" 
        :columns="searchColumns" 
        displayColumn="display"
        :searchResults="searchResults"
        :defaultSearchText="defaultSearchText"
        :processing="processing"
        :enableAutoSearch="enableAutoSearch"
        :showClearSearch="showClearSearch"
        :resetFocus="resetFocus"
        :addToBaseClass="defaultAddToBaseClass"
        @performSearchBase="performSearchBase"
        @onChange="onChange">
    </search-base>
</template>